import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import Clients from './pages/Clients';
import Keywords from './pages/Keywords';
import Leads from './pages/Leads';
import CalendarPage from './pages/CalendarPage';
import Reports from './pages/Reports';
import Consultants from './pages/Consultants';
import Notifications from './pages/Notifications';
import Opportunities from './pages/Opportunities';
import ClientView from './pages/ClientView';
import ClientPortal from './pages/ClientPortal';
import PublicSEAReport from './pages/PublicSEAReport';
import GoogleAnalytics from './pages/GoogleAnalytics';
import SearchConsole from './pages/SearchConsole';
import KeywordEvolution from './pages/KeywordEvolution';
import LeadAnalytics from './pages/LeadAnalytics';
import Settings from './pages/Settings';
import UserManagement from './pages/UserManagement';
import EmailBranding from './pages/EmailBranding';
import Conversions from './pages/Conversions';
import Integrations from './pages/Integrations';
import Setup from './pages/Setup';
import SEADashboard from './pages/sea/SEADashboard';
import SEACampaigns from './pages/sea/SEACampaigns';
import SEALeadAnalytics from './pages/sea/SEALeadAnalytics';
import SEAReports from './pages/sea/SEAReports';
import Pricing from './pages/Pricing';
import Billing from './pages/Billing';
import BillingSuccess from './pages/BillingSuccess';
import Finance from './pages/Finance';
import { ModeProvider } from './contexts/ModeContext';
import SetPassword from './pages/SetPassword';

import './App.css';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-neutral-200 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-neutral-500 font-mono text-sm">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Public Route wrapper (redirect to dashboard if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-neutral-200 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-neutral-500 font-mono text-sm">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
      <Route path="/keywords" element={<ProtectedRoute><Keywords /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/consultants" element={<ProtectedRoute><Consultants /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/opportunities" element={<ProtectedRoute><Opportunities /></ProtectedRoute>} />
      <Route path="/google-analytics" element={<ProtectedRoute><GoogleAnalytics /></ProtectedRoute>} />
      <Route path="/search-console" element={<ProtectedRoute><SearchConsole /></ProtectedRoute>} />
      <Route path="/keyword-evolution" element={<ProtectedRoute><KeywordEvolution /></ProtectedRoute>} />
      <Route path="/lead-analytics" element={<ProtectedRoute><LeadAnalytics /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/email-branding" element={<ProtectedRoute><EmailBranding /></ProtectedRoute>} />
      <Route path="/conversions" element={<ProtectedRoute><Conversions /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute><Setup /></ProtectedRoute>} />

      {/* SEA mode routes */}
      <Route path="/sea/dashboard" element={<ProtectedRoute><SEADashboard /></ProtectedRoute>} />
      <Route path="/sea/campaigns" element={<ProtectedRoute><SEACampaigns /></ProtectedRoute>} />
      <Route path="/sea/lead-analytics" element={<ProtectedRoute><SEALeadAnalytics /></ProtectedRoute>} />
      <Route path="/sea/reports" element={<ProtectedRoute><SEAReports /></ProtectedRoute>} />

      {/* Billing & Pricing */}
      <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
      <Route path="/finance" element={<ProtectedRoute><Finance /></ProtectedRoute>} />
      
      {/* Public routes - Set password (invitation/reset) */}
      <Route path="/set-password" element={<SetPassword />} />
      
      {/* Public routes - Client shared view (no auth required) */}
      <Route path="/client-view/:token" element={<ClientView />} />
      <Route path="/sea-report/:token" element={<PublicSEAReport />} />
      <Route path="/portail-client/:token" element={<ClientPortal />} />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ModeProvider>
          <AppRoutes />
          <Toaster />
        </ModeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
